/**
 * QUIPWA — three-way merge of a note's text
 *
 * When a note changed on this device AND on Drive since the last sync, the
 * two versions are merged line by line against the last version both knew
 * (`base`). Edits in different places combine; edits to the same lines are a
 * conflict, and the caller keeps both versions (the remote one stays in the
 * note, the local one becomes a conflict copy) — text is never dropped.
 *
 * ```ts
 * mergeText('a\nb\nc\nd', 'A\nb\nc\nd', 'a\nb\nc\nD')  // { text: 'A\nb\nc\nD', conflict: false }
 * mergeText('a', 'x', 'y')                              // { text: 'x', conflict: true }
 * ```
 *
 * As in git, edits to adjacent lines (and appends at the same spot) count as
 * a conflict: interleaving them silently could produce nonsense.
 *
 * Line endings are normalised to LF first: the same file edited on Windows
 * elsewhere would otherwise differ on every line.
 */
import { diff3Merge } from 'node-diff3'

export interface TextMergeResult {
  /** The merged text; on conflict, the local text unchanged. */
  text: string
  conflict: boolean
}

const normalise = (s: string) => s.replace(/\r\n?/g, '\n')

export function mergeText(base: string, local: string, remote: string): TextMergeResult {
  const o = normalise(base)
  const a = normalise(local)
  const b = normalise(remote)

  // Fast paths, and the ones diff3 would also produce.
  if (a === b) return { text: a, conflict: false }
  if (a === o) return { text: b, conflict: false }
  if (b === o) return { text: a, conflict: false }

  // `excludeFalseConflicts`: both sides making the same change is not a conflict.
  const regions = diff3Merge(a.split('\n'), o.split('\n'), b.split('\n'), { excludeFalseConflicts: true })
  const out: string[] = []
  for (const region of regions) {
    if (region.conflict) return { text: a, conflict: true }
    if (region.ok) out.push(...region.ok)
  }
  return { text: out.join('\n'), conflict: false }
}
